import React, { useCallback, useEffect, useState } from 'react';
import { Button, Checkbox, Drawer, Form, Grid, Input, Modal, Popconfirm, Space, Spin, Switch, Table, Tag, Tooltip, Typography, message } from 'antd';
import { CheckCircleFilled, PlusOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../../shared/i18n';
import DnsOneClick from '../../../components/DnsOneClick';
import tmService from '../../../services/tmService';
import { fmtTime, ok, toastErr } from './common';

const { Text } = Typography;

const CHECKS = [
    { key: 'dkim', label: 'DKIM', tip: 'Required; sending works only after it is active' },
    { key: 'spf', label: 'SPF' },
    { key: 'return_path', label: 'Return-Path' },
    { key: 'dmarc', label: 'DMARC' },
];

const CheckTags = ({ checks }) => {
    const { t } = useTranslation();
    return (
        <Space size={4} wrap>
            {CHECKS.map((c) => {
                const tag = (
                    <Tag key={c.key} color={checks?.[c.key] ? 'green' : 'default'} icon={checks?.[c.key] ? <CheckCircleFilled /> : null} style={{ marginInlineEnd: 0 }}>
                        {c.label}
                    </Tag>
                );
                return c.tip ? <Tooltip key={c.key} title={t(c.tip)}>{tag}</Tooltip> : tag;
            })}
        </Space>
    );
};

/** 域名 DNS 抽屉：一键 DNS + 验证 + 共用开关 */
const DomainDrawer = ({ domainId, stream, onClose, onChanged }) => {
    const { t } = useTranslation();
    const screens = Grid.useBreakpoint();
    const [d, setD] = useState(null);
    const [verifying, setVerifying] = useState(false);
    const [dnsKey, setDnsKey] = useState(0);

    const load = useCallback(async () => {
        if (!domainId) return;
        try {
            const r = await tmService.getDomain(domainId);
            setD(r?.data || null);
        } catch (e) {
            toastErr(e, t('Failed to load'));
        }
    }, [domainId, t]);

    useEffect(() => { setD(null); load(); }, [load]);

    const verify = async () => {
        setVerifying(true);
        try {
            const r = await tmService.verifyDomain(domainId);
            if (ok(r)) {
                setD(r.data);
                (r.data.verified ? message.success : message.warning)(r.message && r.message !== 'success' ? r.message : (r.data.verified ? t('Verified') : t('DKIM record isn\'t live yet')));
                setDnsKey((k) => k + 1);
                onChanged && onChanged();
            }
        } catch (e) {
            toastErr(e, t('Verification failed'));
        } finally {
            setVerifying(false);
        }
    };

    const setShared = async (shared) => {
        try {
            const r = await tmService.updateDomain(domainId, { stream_id: shared ? null : stream.id });
            if (ok(r)) { load(); onChanged && onChanged(); }
        } catch (e) {
            toastErr(e, t('Operation failed'));
        }
    };

    return (
        <Drawer open={!!domainId} onClose={onClose} width={screens.md ? 760 : '100%'} title={d?.domain || ''} destroyOnClose>
            {!d ? <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div> : (
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Space wrap>
                            {d.verified ? <Tag color="green">{t('Verified')}</Tag> : <Tag color="orange">{t('Unverified')}</Tag>}
                            <CheckTags checks={d.checks} />
                        </Space>
                        <Button type="primary" icon={<SafetyCertificateOutlined />} loading={verifying} onClick={verify}>{t('Verify')}</Button>
                    </div>
                    <DnsOneClick key={`tm-${d.id}-${dnsKey}`} app={d.oneclick?.app || 'tm_domain'} id={d.oneclick?.id || d.id}
                        title={t('DNS records')} onApplied={() => setTimeout(verify, 3000)} />
                    <Space>
                        <Switch size="small" checked={d.stream_id === null} onChange={setShared} />
                        <Text>{t('Shared by all streams')}</Text>
                    </Space>
                    {d.last_check_at && <Text type="secondary" style={{ fontSize: 12 }}>{t('Last checked')}：{fmtTime(d.last_check_at)}</Text>}
                </Space>
            )}
        </Drawer>
    );
};

const Domains = ({ stream, onChange }) => {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [adding, setAdding] = useState(false);
    const [saving, setSaving] = useState(false);
    const [openId, setOpenId] = useState(null);
    const [form] = Form.useForm();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await tmService.listDomains({ stream_id: stream.id });
            setRows(Array.isArray(r?.data) ? r.data : []);
        } catch (e) {
            toastErr(e, t('Failed to load'));
        } finally {
            setLoading(false);
        }
    }, [stream.id, t]);

    useEffect(() => { load(); }, [load]);

    const changed = () => { load(); onChange && onChange(); };

    const add = async () => {
        const v = await form.validateFields();
        setSaving(true);
        try {
            const r = await tmService.createDomain({ domain: v.domain.trim().toLowerCase(), stream_id: v.shared ? null : stream.id });
            if (ok(r)) {
                setAdding(false);
                form.resetFields();
                changed();
                setOpenId(r.data.id);
            }
        } catch (e) {
            toastErr(e, t('Failed to add'));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (d) => {
        try {
            const r = await tmService.deleteDomain(d.id);
            if (ok(r)) { message.success(t('Deleted')); changed(); }
        } catch (e) {
            toastErr(e, t('Deletion failed'));
        }
    };

    const limit = stream.limits?.domains;
    const full = limit !== -1 && limit !== undefined && rows.length >= limit;

    const columns = [
        {
            title: t('Domains'), dataIndex: 'domain',
            render: (v, d) => (
                <Space size={6}>
                    <Typography.Link onClick={() => setOpenId(d.id)}>{v}</Typography.Link>
                    {d.stream_id === null && <Tooltip title={t('Shared by all streams')}><Tag style={{ marginInlineEnd: 0 }}>{t('Shared')}</Tag></Tooltip>}
                </Space>
            ),
        },
        { title: t('Status'), dataIndex: 'verified', width: 90, render: (v) => (v ? <Tag color="green">{t('Verified')}</Tag> : <Tag color="orange">{t('Unverified')}</Tag>) },
        { title: t('Records'), key: 'checks', render: (_, d) => <CheckTags checks={d.checks} />, responsive: ['md'] },
        {
            title: '', key: 'op', width: 140,
            render: (_, d) => (
                <Space size={0}>
                    <Button type="link" size="small" onClick={() => setOpenId(d.id)}>{d.verified ? t('DNS') : t('Verify now')}</Button>
                    <Popconfirm title={t('Once deleted, this domain can no longer send')} okText={t('Delete')} okButtonProps={{ danger: true }} onConfirm={() => remove(d)}>
                        <Button type="link" size="small" danger>{t('Delete')}</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <Space style={{ marginBottom: 12 }} wrap>
                <Tooltip title={full ? t('Plan domain limit reached') : null}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setAdding(true)} disabled={full}>{t('Add domain')}</Button>
                </Tooltip>
                <Text type="secondary">{rows.length} / {limit === -1 ? t('Unlimited') : limit}</Text>
            </Space>
            <Table rowKey="id" size="small" loading={loading} dataSource={rows} columns={columns} pagination={false} />
            <Modal open={adding} title={t('Add domain')} onOk={add} confirmLoading={saving} onCancel={() => setAdding(false)} destroyOnClose>
                <Form form={form} layout="vertical" initialValues={{ shared: false }}>
                    <Form.Item name="domain" label={t('Domains')}
                        rules={[{ required: true, message: t('Please enter a domain') }, { pattern: /^(?!-)[a-z0-9-]+(\.[a-z0-9-]+)+$/i, message: t('Enter a valid domain') }]}>
                        <Input placeholder="mail.example.com" />
                    </Form.Item>
                    <Form.Item name="shared" valuePropName="checked" style={{ marginBottom: 0 }}>
                        <Checkbox>{t('Shared by all streams')}</Checkbox>
                    </Form.Item>
                </Form>
            </Modal>
            <DomainDrawer domainId={openId} stream={stream} onClose={() => setOpenId(null)} onChanged={changed} />
        </div>
    );
};

export default Domains;
